import { useState, useCallback, ReactNode } from "react";
import { ControlContext, ControlContextType } from "./ControlContext";
import { FilterField } from "../config/filter";

function parseInitialTimeRange(): { start: Date | null; end: Date | null } {
  const params = new URLSearchParams(window.location.search);
  const start = params.get("start");
  const end = params.get("end");
  return {
    start: start ? new Date(start) : null,
    end: end ? new Date(end) : null,
  };
}

function syncTimeRangeToURL(range: { start: Date | null; end: Date | null }) {
  const params = new URLSearchParams();
  if (range.start) {
    params.set("start", range.start.toISOString().split("T")[0]);
  }
  if (range.end) {
    params.set("end", range.end.toISOString().split("T")[0]);
  }
  const query = params.toString();
  const newURL = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", newURL);
}

interface ControlProviderProps {
  children: ReactNode;
}

export function ControlProvider({ children }: ControlProviderProps) {
  const [filterField, setFilterField] = useState<FilterField | undefined>(undefined);
  const [filterValue, setFilterValue] = useState<string>("");
  const [usePercent, setUsePercent] = useState<boolean>(false);
  const [timeRange, setTimeRangeState] = useState<{ start: Date | null; end: Date | null }>(parseInitialTimeRange);
  const [grainOverride, setGrainOverride] = useState<import("./ControlContext").TimeGrainOverride>(null);

  const setTimeRange = useCallback((range: { start: Date | null; end: Date | null }) => {
    setTimeRangeState(range);
    syncTimeRangeToURL(range);
  }, []);

  const handleClearFilter = () => {
    setFilterField(undefined);
    setFilterValue("");
  };

  const contextValue: ControlContextType = {
    filterField,
    filterValue,
    usePercent,
    timeRange,
    grainOverride,
    setFilterField,
    setFilterValue,
    setUsePercent,
    setTimeRange,
    setGrainOverride,
    clearFilter: handleClearFilter,
  };

  return <ControlContext.Provider value={contextValue}>{children}</ControlContext.Provider>;
}
